import {
    Card,
    Checkbox,
    CrudMode,
    DataDisplay,
    Grid,
    pn,
    Stack,
    useElementBreakpoints,
    ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import {
    Class,
    ClassEditResource,
    ClassViewResource,
    LkClassLevel,
    LkClassMode,
    LkClassType,
    School,
} from "generated/openApi";
import produce from "immer";

export const BasicInfo = (props: {
    crudMode: CrudMode;
    classItem: Class;
    setClassItem: (value: Class) => void;
    viewResource: ClassViewResource;
    editResource: ClassEditResource;
    validationErrors: ValidationErrors;
}) => {
    const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
    const properties = props.viewResource.properties || [];

    const nameField = () => {
        const propertyName = pn<Class>("name");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.name}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={props.classItem.name}
                onChange={(e) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.name = e.target.value;
                        })
                    )
                }
            />
        );
    };

    const classIdField = () => {
        const propertyName = pn<Class>("identification");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.identification}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={props.classItem.identification}
                onChange={(e) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.identification = e.target.value;
                        })
                    )
                }
            />
        );
    };

    const unitsField = () => {
        const propertyName = pn<Class>("units");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.units}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                type="number"
                errors={props.validationErrors[propertyName]}
                value={props.classItem.units ?? ""}
                onChange={(e) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.units = Number(e.target.value);
                        })
                    )
                }
            />
        );
    };

    const schoolField = () => {
        const propertyName = pn<Class>("schoolId");
        const property = properties.find((x) => x.propertyName === propertyName);
        const selected = props.editResource.schools?.find((x) => x.id === props.classItem.schoolId);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{selected?.name}</DataDisplay>
        ) : (
            <Select
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={selected}
                options={props.editResource.schools}
                getOptionLabel={(x: School) => x.name}
                onChange={(e, value) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.schoolId = value?.id;
                        })
                    )
                }
            />
        );
    };

    const classLevelField = () => {
        const propertyName = pn<Class>("classLevelId");
        const property = properties.find((x) => x.propertyName === propertyName);
        const selected = props.editResource.classLevels?.find((x) => x.id === props.classItem.classLevelId);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{selected?.name}</DataDisplay>
        ) : (
            <Select
                modelProperty={property}
                required
                errors={props.validationErrors[propertyName]}
                value={selected}
                options={props.editResource.classLevels}
                getOptionLabel={(x: LkClassLevel) => x.name}
                onChange={(e, value) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.classLevelId = value?.id;
                        })
                    )
                }
            />
        );
    };

    const classTypeField = () => {
        const propertyName = pn<Class>("classTypeId");
        const property = properties.find((x) => x.propertyName === propertyName);
        const selected = props.editResource.classTypes?.find((x) => x.id === props.classItem.classTypeId);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{selected?.name}</DataDisplay>
        ) : (
            <Select
                modelProperty={property}
                required
                errors={props.validationErrors[propertyName]}
                value={selected}
                options={props.editResource.classTypes}
                getOptionLabel={(x: LkClassType) => x.name}
                onChange={(e, value) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.classTypeId = value?.id;
                        })
                    )
                }
            />
        );
    };

    const classSectionField = () => {
        const propertyName = pn<Class>("classSection");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.classSection}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={props.classItem.classSection}
                onChange={(e) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.classSection = e.target.value;
                        })
                    )
                }
            />
        );
    };

    const onTrackField = () => {
        const propertyName = pn<Class>("onTrack");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.onTrack}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={props.classItem.onTrack}
                onChange={(e) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.onTrack = e.target.value;
                        })
                    )
                }
            />
        );
    };

    const classModeField = () => {
        const propertyName = pn<Class>("classModeId");
        const property = properties.find((x) => x.propertyName === propertyName);
        const selected = props.editResource.classModes?.find((x) => x.id === props.classItem.classModeId);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{selected?.name}</DataDisplay>
        ) : (
            <Select
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={selected}
                options={props.editResource.classModes}
                getOptionLabel={(x: LkClassMode) => x.name}
                onChange={(e, value) =>
                    props.setClassItem(
                        produce(props.classItem, (draft) => {
                            draft.classModeId = value?.id;
                        })
                    )
                }
            />
        );
    };

    const lockedField = () => {
        const propertyName = pn<Class>("isLocked");
        const property = properties.find((x) => x.propertyName === propertyName);

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.classItem.isLocked ? "Yes" : "No"}</DataDisplay>
        ) : (
            <Stack height="100%" justifyContent={"center"}>
                <Checkbox
                    label={property?.displayName}
                    checked={!!props.classItem.isLocked}
                    onChange={(e) =>
                        props.setClassItem(
                            produce(props.classItem, (draft) => {
                                draft.isLocked = e.target.checked;
                            })
                        )
                    }
                />
            </Stack>
        );
    };

    return (
        <Card>
            <Grid ref={breakpointsRef} container spacing="sm">
                <Grid item xs={largeFieldBreakpoints}>{nameField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{classIdField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{unitsField()}</Grid>
                <Grid item xs={largeFieldBreakpoints}>{schoolField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{classLevelField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{classTypeField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{classSectionField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{onTrackField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{classModeField()}</Grid>
                <Grid item xs={smallFieldBreakpoints}>{lockedField()}</Grid>
            </Grid>
        </Card>
    );
};
